import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnSort } from '@tanstack/react-table';
import useRouteParamQuery from '@Hooks/useRouteParamQuery';
import useSortingConfig from '@Hooks/useSortingConfig';
import { useTypedSelector } from '@Store/hooks';
import hasErrorBoundary from '@Components/common/hasErrorBoundary';
import NestedDataTable from '@Components/ExploreByProgramme/NestedDataTable';
import ToolTip from '@Components/RadixComponents/ToolTip';
import {
  mapSortbyParamValue,
  reverseSortbyParamValue,
} from '@Constants/programmeDetails';
import { getProgrammeDetailsByProvince } from '@Services/provincialProfile';
import { convertToCurrencySystem } from '@Utils/index';

const columns = [
  {
    header: 'PROGRAMME',
    accessorKey: 'programs',
    headerId: 'program',
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: 'PROJECT',
    accessorKey: 'project_name',
    headerId: 'project',
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: 'FIRST TIER PARTNER',
    accessorKey: 'first_tier_partner',
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: 'SECTOR',
    accessorKey: 'sector',
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: 'WORKING PROVINCE',
    accessorKey: 'working_province',
    headerId: 'working_area',
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: 'MARKERS',
    accessorKey: 'marker',
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: 'BUDGET ALLOCATED (£)',
    accessorKey: 'budget',
    cell: ({ childRowData }: Record<string, any>) => {
      return convertToCurrencySystem(childRowData?.budget);
    },
    className: 'naxatw-min-w-[18rem]',
  },
  {
    header: '',
    accessorKey: 'icon',
    size: 35,
    cell: ({ parentRowData, childRowData }: Record<string, any>) => {
      return (
        <Link
          to={`/explore-by/programmes/${parentRowData?.program_id}/?componentId=${childRowData?.project_id}`}
        >
          <ToolTip
            name="open_in_new"
            className="naxatw-rounded-full naxatw-p-1 naxatw-text-xl naxatw-text-primary-600 naxatw-duration-300 hover:naxatw-bg-primary-200"
            message="Project Details"
            preventDefault={false}
          />
        </Link>
      );
    },
  },
];

const ProgrammeDetailsTable = () => {
  const { provinceId } = useParams();
  const [filterParams] = useSearchParams();
  const getQueryParams = useRouteParamQuery();
  const municipalityCode = getQueryParams.get('municipality');
  const filterBy = useTypedSelector(state => state.provincialProfile.filterBy);
  const [sorting, setSorting] = useState<ColumnSort>({ id: '', desc: false });

  const { sortBy, sortOrderKey, sortDir } = useSortingConfig(
    sorting?.id,
    sorting.desc,
  );

  // use filter store of programme and clear on unmount
  const filterFields = useTypedSelector(
    state => state.programme.report?.filterParams,
  );

  // filter section
  const programmeFilter = filterFields?.program || [];
  const FirstTierPartnerFilter = filterFields?.first_tier_partner || [];
  const projectFilter = filterFields?.project || [];
  const sectorFilter = filterFields?.sector || [];
  const markerFilter = filterFields?.marker || [];

  const { data, isLoading } = useQuery({
    queryKey: [
      'programme-details-table-data',
      provinceId,
      municipalityCode,
      filterBy,
      sortBy,
      sortDir,
      // Header filters
      projectFilter,
      programmeFilter,
      FirstTierPartnerFilter,
      sectorFilter,
      markerFilter,
    ],
    queryFn: async () =>
      getProgrammeDetailsByProvince({
        province: provinceId,
        municipality: municipalityCode,
        status: filterBy,
        [sortOrderKey]: sortDir,
        sort_by: sortBy,
        // Header filters
        program: programmeFilter?.join(','),
        project: projectFilter?.join(','), // project
        first_tier_partner: FirstTierPartnerFilter?.join(','),
        sector: sectorFilter?.join(','),
        marker: markerFilter?.join(','),
      }),
    select: res => res.data,
  });

  const isFilterActive = filterParams.get('filter') === 'true';

  return (
    <div className="scrollbar naxatw-max-h-[calc(90dvh-10rem)] naxatw-overflow-auto">
      <NestedDataTable
        columns={columns}
        data={data}
        isFilterActive={isFilterActive}
        mainDataKey="programs"
        nestingDataKey="data"
        isLoading={isLoading}
        sortingState={{ ...sorting, id: reverseSortbyParamValue(sorting.id) }}
        onSorting={opt =>
          setSorting({ id: mapSortbyParamValue(opt?.id), desc: opt.desc })
        }
      />
    </div>
  );
};

export default hasErrorBoundary(ProgrammeDetailsTable);
